/**
 * VGrove Autoroutes 集成工具
 * 提供与 @gulibs/vgrove-autoroutes 的深度集成支持
 * 
 * 版本: 2.1.0
 * 更新: 增强错误处理、类型安全、性能优化和配置验证
 */

import type { LoaderFunction, LoaderFunctionArgs } from 'react-router';
import { middlewareManager, type MiddlewareConfig } from './middleware';
import { createAuthLoader, createRoleLoader, createPermissionLoader, createCombinedLoader, type LoaderOptions } from './loaders';
import type { BaseUser, AuthOptions, ClientRouteConfigs } from './types';
import { debug } from './debug';

// ============= 常量定义 =============
const INTEGRATION_VERSION = '2.1.0';
const DEFAULT_CACHE_TTL = 300000; // 5分钟
const MAX_CACHE_SIZE = 100;
const MAX_RETRY_ATTEMPTS = 3;

// ============= 路由配置分析器 =============

/**
 * 路由配置分析器
 * 分析和验证路由配置的完整性和依赖关系
 */
class RouteConfigAnalyzer {
  /**
   * 分析路由配置的依赖关系
   */
  static analyzeDependencies<TUser extends BaseUser = BaseUser>(
    routeConfigs: Map<string, ClientRouteConfigs<TUser>>
  ): {
    authRoutes: string[];
    roleRoutes: Map<string, string[]>;
    permissionRoutes: Map<string, string[]>;
    conflicts: string[];
    recommendations: string[];
  } {
    const authRoutes: string[] = [];
    const roleRoutes = new Map<string, string[]>();
    const permissionRoutes = new Map<string, string[]>();
    const conflicts: string[] = [];
    const recommendations: string[] = [];

    for (const [routePath, config] of routeConfigs) {
      if (config.auth) {
        authRoutes.push(routePath);

        // 检查角色配置
        if (config.auth.roles && config.auth.roles.length > 0) {
          roleRoutes.set(routePath, config.auth.roles);
        }

        // 检查权限配置
        if (config.auth.permissions && config.auth.permissions.length > 0) {
          permissionRoutes.set(routePath, config.auth.permissions);
        }

        // 检查配置冲突
        if (config.auth.roles && config.auth.permissions) {
          if (config.auth.roles.length > 0 && config.auth.permissions.length > 0) {
            conflicts.push(`路由 ${routePath} 同时配置了角色和权限，可能导致访问控制过于严格`);
          }
        }

        // 生成建议
        if (!config.auth.redirectTo) {
          recommendations.push(`路由 ${routePath} 建议配置 redirectTo 属性以提供更好的用户体验`);
        }
      }

      // 检查守卫配置
      if (config.guard && config.auth) {
        conflicts.push(`路由 ${routePath} 同时配置了 auth 和 guard，可能导致重复验证`);
      }
    }

    return {
      authRoutes,
      roleRoutes,
      permissionRoutes,
      conflicts,
      recommendations
    };
  }

  /**
   * 验证路由配置的完整性
   */
  static validateConfigs<TUser extends BaseUser = BaseUser>(
    routeConfigs: Map<string, ClientRouteConfigs<TUser>>
  ): {
    valid: boolean;
    errors: string[];
    warnings: string[];
  } {
    const errors: string[] = [];
    const warnings: string[] = [];

    for (const [routePath, config] of routeConfigs) {
      // 验证认证配置
      if (config.auth) {
        if (config.auth.check && typeof config.auth.check !== 'function') {
          errors.push(`路由 ${routePath} 的 auth.check 必须是一个函数`);
        }

        if (config.auth.roles && !Array.isArray(config.auth.roles)) {
          errors.push(`路由 ${routePath} 的 auth.roles 必须是字符串数组`);
        }

        if (config.auth.permissions && !Array.isArray(config.auth.permissions)) {
          errors.push(`路由 ${routePath} 的 auth.permissions 必须是字符串数组`);
        }

        if (!config.auth.redirectTo && !config.auth.errorMessage) {
          warnings.push(`路由 ${routePath} 建议配置 redirectTo 或 errorMessage 以提供用户反馈`);
        }
      }

      // 验证守卫配置
      if (config.guard) {
        if (!config.guard.condition || typeof config.guard.condition !== 'function') {
          errors.push(`路由 ${routePath} 的 guard.condition 必须是一个函数`);
        }
      }
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings
    };
  }
}

// ============= 性能优化器 =============

/**
 * 路由性能优化器
 * 优化路由配置以提升性能
 */
class RoutePerformanceOptimizer {
  private static loaderCache = new Map<string, any>();
  private static actionCache = new Map<string, any>();

  /**
   * 缓存 loader 结果
   */
  static createCachedLoader(
    originalLoader: LoaderFunction,
    cacheKey: string,
    ttl: number = DEFAULT_CACHE_TTL
  ): LoaderFunction {
    return async (args: LoaderFunctionArgs) => {
      const cached = this.loaderCache.get(cacheKey);
      const now = Date.now();

      if (cached && (now - cached.timestamp) < ttl) {
        debug.routing('使用缓存的 loader 结果', { cacheKey });
        return cached.data;
      }

      const result = await originalLoader(args);

      this.loaderCache.set(cacheKey, {
        data: result,
        timestamp: now
      });

      debug.routing('loader 结果已缓存', { cacheKey, ttl });
      return result;
    };
  }

  /**
   * 清除缓存
   */
  static clearCache(): void {
    this.loaderCache.clear();
    this.actionCache.clear();
    debug.routing('路由缓存已清除');
  }

  /**
   * 优化路由配置
   */
  static optimizeRoutes(routes: any[], options?: {
    enableLoaderCache?: boolean;
    cacheStrategy?: 'memory' | 'session' | 'local';
    maxCacheSize?: number;
  }): any[] {
    const { enableLoaderCache = true, maxCacheSize = MAX_CACHE_SIZE } = options || {};

    return routes.map(route => {
      const optimizedRoute = { ...route };

      // 优化 loader
      if (enableLoaderCache && route.loader) {
        const cacheKey = `loader-${route.path}`;
        optimizedRoute.loader = this.createCachedLoader(route.loader, cacheKey);
      }

      // 递归优化子路由
      if (route.children && Array.isArray(route.children)) {
        optimizedRoute.children = this.optimizeRoutes(route.children, options);
      }

      // 限制缓存大小
      if (this.loaderCache.size > maxCacheSize) {
        const oldestKey = this.loaderCache.keys().next().value;
        if (oldestKey !== undefined) {
          this.loaderCache.delete(oldestKey);
        }
      }

      return optimizedRoute;
    });
  }
}

// ============= 路由配置读取器 =============

/**
 * 路由配置解析器
 * 用于解析 vgrove-autoroutes 扫描到的路由配置文件
 */
export class RouteConfigReader {
  private static configCache = new Map<string, ClientRouteConfigs<any>>();

  /**
   * 读取并解析路由配置文件（带重试机制）
   * @param configPath 配置文件路径
   * @returns 解析后的配置对象
   */
  static async readConfig<TUser extends BaseUser = BaseUser>(
    configPath: string
  ): Promise<ClientRouteConfigs<TUser> | null> {
    // 检查缓存
    if (this.configCache.has(configPath)) {
      const cached = this.configCache.get(configPath);
      return cached as ClientRouteConfigs<TUser> | null;
    }

    let lastError: Error | null = null;

    // 重试机制
    for (let attempt = 1; attempt <= MAX_RETRY_ATTEMPTS; attempt++) {
      try {
        // 动态导入配置文件
        const configModule = await import(/* @vite-ignore */ configPath);
        const config = configModule.default || configModule;

        // 验证配置格式
        if (typeof config !== 'object' || config === null) {
          debug.routing('无效的路由配置文件格式', { configPath, attempt });
          return null;
        }

        debug.routing('成功读取路由配置', { configPath, config, attempt });

        // 缓存配置
        this.configCache.set(configPath, config);
        return config as ClientRouteConfigs<TUser>;
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));

        debug.routing(`读取路由配置失败 (尝试 ${attempt}/${MAX_RETRY_ATTEMPTS})`, {
          configPath,
          error: lastError.message,
          attempt
        });

        // 如果不是最后一次尝试，等待一段时间后重试
        if (attempt < MAX_RETRY_ATTEMPTS) {
          await new Promise(resolve => setTimeout(resolve, 100 * attempt));
        }
      }
    }

    debug.routing('所有重试尝试都失败了', {
      configPath,
      attempts: MAX_RETRY_ATTEMPTS,
      lastError: lastError?.message
    });

    return null;
  }

  /**
   * 清除配置缓存
   */
  static clearCache(): void {
    this.configCache.clear();
  }

  /**
   * 合并多个配置对象
   * @param configs 配置对象数组，按优先级排序（后面的会覆盖前面的）
   * @returns 合并后的配置
   */
  static mergeConfigs<TUser extends BaseUser = BaseUser>(
    ...configs: (ClientRouteConfigs<TUser> | null)[]
  ): ClientRouteConfigs<TUser> {
    const result: ClientRouteConfigs<TUser> = {};

    for (const config of configs) {
      if (!config) continue;

      // 合并认证配置
      if (config.auth) {
        result.auth = { ...result.auth, ...config.auth };
      }

      // 合并守卫配置
      if (config.guard) {
        result.guard = { ...result.guard, ...config.guard };
      }

      // 覆盖组件（最后的优先）
      if (config.error) {
        result.error = config.error;
      }

      if (config.loading) {
        result.loading = config.loading;
      }

      // 合并状态渲染器
      if (config.stateRenderers) {
        result.stateRenderers = { ...result.stateRenderers, ...config.stateRenderers };
      }

      // 合并其他配置项
      Object.keys(config).forEach(key => {
        if (!['auth', 'guard', 'error', 'loading', 'stateRenderers'].includes(key)) {
          (result as any)[key] = (config as any)[key];
        }
      });
    }

    return result;
  }
}

// ============= Loader 集成器 =============

/**
 * 为 vgrove-autoroutes 创建集成的 loader
 */
export class LoaderIntegrator {
  /**
   * 根据路由配置创建认证 loader
   */
  static createAuthLoaderFromConfig<TUser extends BaseUser = BaseUser>(
    config: AuthOptions<TUser>,
    globalOptions?: LoaderOptions<TUser>
  ): LoaderFunction {
    const loaderOptions: Partial<LoaderOptions<TUser>> = {
      ...globalOptions,
      loginPath: config.redirectTo || globalOptions?.loginPath,
      publicPaths: config.publicPaths || globalOptions?.publicPaths
    };

    if (config.check) {
      loaderOptions.authCheck = config.check as any;
    }

    return createAuthLoader<TUser>(loaderOptions);
  }

  /**
   * 根据路由配置创建角色 loader
   */
  static createRoleLoaderFromConfig<TUser extends BaseUser = BaseUser>(
    config: AuthOptions<TUser>,
    globalOptions?: LoaderOptions<TUser>
  ): LoaderFunction | null {
    if (!config.roles || config.roles.length === 0) {
      return null;
    }

    const loaderOptions: Partial<LoaderOptions<TUser>> = {
      ...globalOptions,
      loginPath: config.redirectTo || globalOptions?.loginPath,
      forbiddenPath: config.redirectTo || globalOptions?.forbiddenPath
    };

    if (config.check) {
      loaderOptions.authCheck = config.check as any;
    }

    return createRoleLoader<TUser>(config.roles, loaderOptions);
  }

  /**
   * 根据路由配置创建权限 loader
   */
  static createPermissionLoaderFromConfig<TUser extends BaseUser = BaseUser>(
    config: AuthOptions<TUser>,
    globalOptions?: LoaderOptions<TUser>
  ): LoaderFunction | null {
    if (!config.permissions || config.permissions.length === 0) {
      return null;
    }

    const loaderOptions: Partial<LoaderOptions<TUser>> = {
      ...globalOptions,
      loginPath: config.redirectTo || globalOptions?.loginPath,
      forbiddenPath: config.redirectTo || globalOptions?.forbiddenPath
    };

    if (config.check) {
      loaderOptions.authCheck = config.check as any;
    }

    return createPermissionLoader<TUser>(config.permissions, loaderOptions);
  }

  /**
   * 根据路由配置创建组合 loader
   */
  static createCombinedLoaderFromConfig<TUser extends BaseUser = BaseUser>(
    config: AuthOptions<TUser>,
    globalOptions?: LoaderOptions<TUser>
  ): LoaderFunction {
    const loaderOptions: Partial<LoaderOptions<TUser>> = {
      ...globalOptions,
      loginPath: config.redirectTo || globalOptions?.loginPath,
      forbiddenPath: config.redirectTo || globalOptions?.forbiddenPath
    };

    if (config.check) {
      loaderOptions.authCheck = config.check as any;
    }

    const combinedConfig = {
      roles: config.roles,
      permissions: config.permissions,
      requireAll: false // 默认只需要满足其中一个条件
    };

    return createCombinedLoader<TUser>(combinedConfig, loaderOptions);
  }

  /**
   * 根据完整的路由配置创建 loader
   */
  static createLoaderFromRouteConfig<TUser extends BaseUser = BaseUser>(
    routeConfig: ClientRouteConfigs<TUser>,
    globalOptions?: LoaderOptions<TUser>
  ): LoaderFunction | null {
    if (!routeConfig.auth) {
      return null;
    }

    const config = routeConfig.auth;

    // 如果有角色或权限要求，创建组合 loader
    if ((config.roles && config.roles.length > 0) || (config.permissions && config.permissions.length > 0)) {
      return this.createCombinedLoaderFromConfig(config, globalOptions);
    }

    // 否则创建基础认证 loader
    return this.createAuthLoaderFromConfig(config, globalOptions);
  }
}

// ============= 中间件集成器 =============

/**
 * 中间件集成器
 * 用于将路由配置转换为中间件注册
 */
export class MiddlewareIntegrator {
  /**
   * 根据路由配置注册中间件
   */
  static registerMiddlewareFromConfig<TUser extends BaseUser = BaseUser>(
    routePath: string,
    routeConfig: ClientRouteConfigs<TUser>
  ): void {
    if (!routeConfig.auth) {
      return;
    }

    const config = routeConfig.auth;

    // 注册认证中间件
    if (config.check) {
      const authMiddleware: MiddlewareConfig = {
        name: `auth-${routePath}`,
        priority: 100,
        enabled: true,
        pathPatterns: [routePath],
        handler: async (context) => {
          const user = await config.check!(context as any);

          if (!user) {
            return {
              continue: false,
              error: config.errorMessage || '用户未认证，请先登录'
            };
          }

          return {
            continue: true,
            data: { user }
          };
        }
      };

      middlewareManager.register(authMiddleware);
    }

    // 注册角色中间件
    if (config.roles && config.roles.length > 0) {
      const roleMiddleware: MiddlewareConfig = {
        name: `role-${routePath}`,
        priority: 200,
        enabled: true,
        pathPatterns: [routePath],
        handler: async (context) => {
          if (!context.user) {
            return {
              continue: false,
              error: '用户信息不存在，无法进行角色检查'
            };
          }

          const userRoles = context.user.roles || [];
          const hasRole = config.roles!.some(role => userRoles.includes(role));

          if (!hasRole) {
            return {
              continue: false,
              error: `需要角色: ${config.roles!.join(', ')}`
            };
          }

          return {
            continue: true,
            data: { roleCheck: true }
          };
        }
      };

      middlewareManager.register(roleMiddleware);
    }

    // 注册权限中间件
    if (config.permissions && config.permissions.length > 0) {
      const permissionMiddleware: MiddlewareConfig = {
        name: `permission-${routePath}`,
        priority: 300,
        enabled: true,
        pathPatterns: [routePath],
        handler: async (context) => {
          if (!context.user) {
            return {
              continue: false,
              error: '用户信息不存在，无法进行权限检查'
            };
          }

          const userPermissions = context.user.permissions || [];
          const hasPermission = config.permissions!.some(permission => userPermissions.includes(permission));

          if (!hasPermission) {
            return {
              continue: false,
              error: `需要权限: ${config.permissions!.join(', ')}`
            };
          }

          return {
            continue: true,
            data: { permissionCheck: true }
          };
        }
      };

      middlewareManager.register(permissionMiddleware);
    }
  }
}

// ============= 路由增强器 =============

/**
 * 路由增强器
 * 为生成的路由添加额外功能
 */
export class RouteEnhancer {
  /**
   * 增强路由配置，添加认证和中间件支持
   */
  static enhanceRoute<TUser extends BaseUser = BaseUser>(
    route: any,
    routePath: string,
    config?: ClientRouteConfigs<TUser>,
    globalOptions?: LoaderOptions<TUser>
  ): any {
    if (!config) return route;

    const enhancedRoute = { ...route };

    // 增强 loader
    if (config.auth && !enhancedRoute.loader) {
      const authLoader = LoaderIntegrator.createLoaderFromRouteConfig(config, globalOptions);
      if (authLoader) {
        enhancedRoute.loader = AutoroutesIntegration.createIntegratedLoader(authLoader);
        debug.routing('为路由添加认证 loader', { routePath });
      }
    } else if (enhancedRoute.loader && config.auth) {
      // 包装现有 loader
      const originalLoader = enhancedRoute.loader;
      const authLoader = LoaderIntegrator.createLoaderFromRouteConfig(config, globalOptions);
      if (authLoader) {
        enhancedRoute.loader = AutoroutesIntegration.createIntegratedLoader(
          async (args: any) => {
            const authResult = await authLoader(args);
            const originalResult = await originalLoader(args);
            if (authResult && originalResult) {
              return { ...authResult, ...originalResult };
            }
            return authResult || originalResult;
          }
        );
        debug.routing('包装现有 loader 添加认证', { routePath });
      }
    }

    // 增强 action
    if (enhancedRoute.action && config.auth) {
      const originalAction = enhancedRoute.action;
      enhancedRoute.action = async (args: any) => {
        // 先执行中间件检查
        const middlewareResult = await middlewareManager.execute(args);
        if (!middlewareResult.success) {
          if (middlewareResult.response) {
            throw middlewareResult.response;
          }
          throw new Response(null, { status: 403 });
        }

        // 再执行原始 action
        return await originalAction(args);
      };
      debug.routing('为路由添加认证 action', { routePath });
    }

    // 增强错误边界
    if (config.error && !enhancedRoute.errorElement) {
      enhancedRoute.errorElement = config.error;
    }

    // 增强子路由
    if (enhancedRoute.children && Array.isArray(enhancedRoute.children)) {
      enhancedRoute.children = enhancedRoute.children.map((child: any) =>
        this.enhanceRoute(child, `${routePath}/${child.path || ''}`, config, globalOptions)
      );
    }

    return enhancedRoute;
  }

  /**
   * 批量增强路由数组
   */
  static enhanceRoutes<TUser extends BaseUser = BaseUser>(
    routes: any[],
    routeConfigs: Map<string, ClientRouteConfigs<TUser>>,
    globalOptions?: LoaderOptions<TUser>
  ): any[] {
    return routes.map(route => {
      const config = routeConfigs.get(route.path);
      return this.enhanceRoute(route, route.path, config, globalOptions);
    });
  }
}

// ============= 路由工厂 =============

/**
 * 智能路由工厂
 * 根据不同场景创建优化的路由配置
 */
export class SmartRouteFactory {
  /**
   * 创建认证保护的路由
   */
  static createProtectedRoute<TUser extends BaseUser = BaseUser>(
    path: string,
    component: any,
    authConfig: AuthOptions<TUser>,
    options?: {
      layout?: any;
      errorBoundary?: any;
      loading?: any;
    }
  ): any {
    const route: any = {
      path,
      element: component,
      loader: LoaderIntegrator.createAuthLoaderFromConfig(authConfig)
    };

    if (options?.layout) {
      route.element = options.layout;
      route.children = [{ index: true, element: component }];
    }

    if (options?.errorBoundary) {
      route.errorElement = options.errorBoundary;
    }

    if (options?.loading) {
      route.loader = AutoroutesIntegration.createIntegratedLoader(route.loader);
    }

    return route;
  }

  /**
   * 创建角色保护的路由
   */
  static createRoleProtectedRoute<TUser extends BaseUser = BaseUser>(
    path: string,
    component: any,
    roles: string[],
    authConfig?: AuthOptions<TUser>,
    options?: {
      layout?: any;
      errorBoundary?: any;
    }
  ): any {
    const config: AuthOptions<TUser> = {
      ...authConfig,
      roles
    };

    return this.createProtectedRoute(path, component, config, options);
  }

  /**
   * 创建权限保护的路由
   */
  static createPermissionProtectedRoute<TUser extends BaseUser = BaseUser>(
    path: string,
    component: any,
    permissions: string[],
    authConfig?: AuthOptions<TUser>,
    options?: {
      layout?: any;
      errorBoundary?: any;
    }
  ): any {
    const config: AuthOptions<TUser> = {
      ...authConfig,
      permissions
    };

    return this.createProtectedRoute(path, component, config, options);
  }
}

// ============= 集成助手函数 =============

/**
 * 为 vgrove-autoroutes 提供的集成助手
 * 这些函数会被 autoroutes 生成的代码调用
 */
export const AutoroutesIntegration = {
  /**
   * 初始化路由配置集成
   * @param routeConfigs 路由配置映射 (路径 -> 配置文件路径)
   * @param globalLoaderOptions 全局 loader 选项
   */
  async initializeRouteConfigs<TUser extends BaseUser = BaseUser>(
    routeConfigs: Map<string, string>,
    globalLoaderOptions?: LoaderOptions<TUser>
  ): Promise<Map<string, LoaderFunction>> {
    const loaderMap = new Map<string, LoaderFunction>();

    debug.routing('开始初始化路由配置集成', {
      routeCount: routeConfigs.size,
      hasGlobalOptions: !!globalLoaderOptions
    });

    for (const [routePath, configPath] of routeConfigs) {
      try {
        // 读取配置文件
        const config = await RouteConfigReader.readConfig<TUser>(configPath);

        if (!config) {
          continue;
        }

        // 创建 loader
        const loader = LoaderIntegrator.createLoaderFromRouteConfig(config, globalLoaderOptions);

        if (loader) {
          loaderMap.set(routePath, loader);
          debug.routing('为路由创建了 loader', { routePath, configPath });
        }

        // 注册中间件
        MiddlewareIntegrator.registerMiddlewareFromConfig(routePath, config);

      } catch (error) {
        debug.routing('处理路由配置时出错', {
          routePath,
          configPath,
          error: error instanceof Error ? error.message : String(error)
        });
      }
    }

    debug.routing('路由配置集成完成', {
      loadersCreated: loaderMap.size,
      middlewaresRegistered: middlewareManager.getMiddlewares().length
    });

    return loaderMap;
  },

  /**
   * 创建集成的 loader，包含中间件支持
   * @param originalLoader 原始 loader
   */
  createIntegratedLoader(
    originalLoader?: LoaderFunction
  ): LoaderFunction {
    return async (args: LoaderFunctionArgs) => {
      // 执行中间件链
      const middlewareResult = await middlewareManager.execute(args);

      if (!middlewareResult.success) {
        if (middlewareResult.response) {
          throw middlewareResult.response;
        }
        throw new Response(null, { status: 403 });
      }

      // 执行原始 loader
      let loaderResult = null;
      if (originalLoader) {
        loaderResult = await originalLoader(args);
      }

      // 合并中间件数据和 loader 结果
      const result: any = {};
      if (middlewareResult.data) {
        Object.assign(result, middlewareResult.data);
      }
      if (loaderResult) {
        Object.assign(result, loaderResult);
      }
      return result;
    };
  },

  /**
   * 获取路由配置管理器
   */
  getRouteConfigReader() {
    return RouteConfigReader;
  },

  /**
   * 获取 loader 集成器
   */
  getLoaderIntegrator() {
    return LoaderIntegrator;
  },

  /**
   * 获取中间件集成器
   */
  getMiddlewareIntegrator() {
    return MiddlewareIntegrator;
  },

  /**
   * 获取路由增强器
   */
  getRouteEnhancer() {
    return RouteEnhancer;
  },

  /**
   * 获取智能路由工厂
   */
  getSmartRouteFactory() {
    return SmartRouteFactory;
  },

  /**
   * 获取路由配置分析器
   */
  getRouteConfigAnalyzer() {
    return RouteConfigAnalyzer;
  },

  /**
   * 获取路由性能优化器
   */
  getRoutePerformanceOptimizer() {
    return RoutePerformanceOptimizer;
  },

  /**
   * 增强路由数组（便捷方法）
   */
  enhanceRoutes<TUser extends BaseUser = BaseUser>(
    routes: any[],
    routeConfigs: Map<string, ClientRouteConfigs<TUser>>,
    globalOptions?: LoaderOptions<TUser>
  ): any[] {
    return RouteEnhancer.enhanceRoutes(routes, routeConfigs, globalOptions);
  },

  /**
   * 分析路由配置（便捷方法）
   */
  analyzeConfigs<TUser extends BaseUser = BaseUser>(
    routeConfigs: Map<string, ClientRouteConfigs<TUser>>
  ) {
    return RouteConfigAnalyzer.analyzeDependencies(routeConfigs);
  },

  /**
   * 验证路由配置（便捷方法）
   */
  validateConfigs<TUser extends BaseUser = BaseUser>(
    routeConfigs: Map<string, ClientRouteConfigs<TUser>>
  ) {
    return RouteConfigAnalyzer.validateConfigs(routeConfigs);
  },

  /**
   * 优化路由性能（便捷方法）
   */
  optimizeRoutes(routes: any[], options?: Parameters<typeof RoutePerformanceOptimizer.optimizeRoutes>[1]) {
    return RoutePerformanceOptimizer.optimizeRoutes(routes, options);
  },

  /**
   * 创建完整的集成方案
   * @param routes 原始路由数组
   * @param configPaths 路由配置文件路径映射
   * @param globalOptions 全局选项
   * @returns 增强后的路由数组
   */
  async createFullIntegration<TUser extends BaseUser = BaseUser>(
    routes: any[],
    configPaths: Map<string, string>,
    globalOptions?: LoaderOptions<TUser>
  ): Promise<any[]> {
    debug.routing('开始创建完整集成方案', {
      routeCount: routes.length,
      configCount: configPaths.size
    });

    // 1. 初始化路由配置
    const loaderMap = await this.initializeRouteConfigs(configPaths, globalOptions);

    // 2. 读取所有配置文件
    const routeConfigs = new Map<string, ClientRouteConfigs<TUser>>();
    for (const [routePath, configPath] of configPaths) {
      const config = await RouteConfigReader.readConfig<TUser>(configPath);
      if (config) {
        routeConfigs.set(routePath, config);
      }
    }

    // 3. 增强路由
    const enhancedRoutes = RouteEnhancer.enhanceRoutes(routes, routeConfigs, globalOptions);

    debug.routing('完整集成方案创建完成', {
      originalRouteCount: routes.length,
      enhancedRouteCount: enhancedRoutes.length,
      loadersCreated: loaderMap.size,
      middlewaresRegistered: middlewareManager.getMiddlewares().length
    });

    return enhancedRoutes;
  }
};

// ============= 导出所有集成工具 =============

/**
 * 获取集成工具版本信息
 */
export const getIntegrationVersion = () => INTEGRATION_VERSION;

// 默认导出集成助手
export default AutoroutesIntegration;